import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Pencil, Save, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { isAdmin } from '@/lib/auth';
import api from '@/lib/api';

const BAR_COLORS = { CL: 'bg-blue-500', EL: 'bg-emerald-500', SL: 'bg-amber-500' };
const LABELS = { CL: 'Casual Leave', EL: 'Earned Leave', SL: 'Sick Leave' };

export function LeaveEntitlement({ balances }) {
  if (!balances) return <p className="text-sm text-slate-400 p-6">No balance data</p>;
  const cards = [
    { key: 'CL', data: balances.casual_leave },
    { key: 'EL', data: balances.earned_leave },
    { key: 'SL', data: balances.sick_leave },
  ];
  return (
    <div className="space-y-4" data-testid="leave-entitlement">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {cards.map(c => (
          <Card key={c.key} className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-slate-500 uppercase">{LABELS[c.key]}</p>
                <Badge className={`text-[10px] ${c.key === 'CL' ? 'bg-blue-100 text-blue-700' : c.key === 'EL' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'} hover:opacity-90`}>{c.key}</Badge>
              </div>
              <p className="text-3xl font-semibold text-[#0B1B33] font-tabular">{c.data?.balance ?? 0}</p>
              <p className="text-xs text-slate-400 mt-0.5">of {c.data?.entitled ?? 0} entitled</p>
              <div className="mt-3 h-2 bg-slate-100 rounded-full overflow-hidden">
                <div className={`h-full ${BAR_COLORS[c.key]} rounded-full transition-all`} style={{ width: `${c.data?.entitled > 0 ? (c.data.balance / c.data.entitled) * 100 : 0}%` }} />
              </div>
              <div className="flex justify-between mt-1.5 text-[10px] text-slate-400">
                <span>Used: {c.data?.used ?? 0}</span>
                <span>Balance: {c.data?.balance ?? 0}</span>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

export function LeaveRegister({ requests }) {
  if (!requests || requests.length === 0) return <p className="text-sm text-slate-400 p-8 text-center">No leave records found</p>;
  const statusColors = {
    pending: 'bg-amber-100 text-amber-700',
    approved: 'bg-emerald-100 text-emerald-700',
    rejected: 'bg-red-100 text-red-700',
    cancelled: 'bg-slate-100 text-slate-500',
  };
  return (
    <div data-testid="leave-register">
      <Table>
        <TableHeader>
          <TableRow className="bg-slate-50">
            <TableHead>Employee</TableHead>
            <TableHead>Date Applied</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>From</TableHead>
            <TableHead>To</TableHead>
            <TableHead>Duration</TableHead>
            <TableHead>Reason</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Approver</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {requests.map(r => (
            <TableRow key={r.id} data-testid={`register-row-${r.id}`} className="align-top">
              <TableCell className="py-3">
                <p className="text-sm font-medium text-[#0B1B33]" data-testid={`register-employee-${r.id}`}>{r.employee_name || '-'}</p>
                {r.department && <p className="text-[10px] text-slate-400">{r.department}</p>}
              </TableCell>
              <TableCell className="text-xs text-slate-500 font-tabular">{r.created_at?.split('T')[0] || '-'}</TableCell>
              <TableCell><Badge className="bg-[#0D2850]/10 text-[#0D2850] hover:bg-[#0D2850]/10 text-[10px]">{r.leave_type}</Badge></TableCell>
              <TableCell className="text-sm font-tabular">{r.start_date}</TableCell>
              <TableCell className="text-sm font-tabular">{r.end_date}</TableCell>
              <TableCell className="text-sm font-medium">{r.days} {r.half_day ? '(Half)' : 'day(s)'}</TableCell>
              <TableCell className="text-sm text-slate-600 max-w-[280px] align-top" title={r.reason || ''} data-testid={`register-reason-${r.id}`}>
                <span className="block whitespace-normal break-words line-clamp-3">{r.reason}</span>
              </TableCell>
              <TableCell><Badge className={`text-[10px] ${statusColors[r.status] || ''} hover:opacity-90`}>{r.status}</Badge></TableCell>
              <TableCell className="text-xs text-slate-500">{r.approved_by || r.rejected_by || '-'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export function AllBalancesTable() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [departments, setDepartments] = useState([]);
  const [deptFilter, setDeptFilter] = useState('All');
  const [editEmp, setEditEmp] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [effectiveMonth, setEffectiveMonth] = useState('');
  const [saving, setSaving] = useState(false);
  const [auditEmp, setAuditEmp] = useState(null);
  const [auditData, setAuditData] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const admin = isAdmin();

  const loadData = () => {
    setLoading(true);
    Promise.all([
      api.get('/api/leave/all-balances'),
      api.get('/api/leave/departments'),
    ]).then(([balRes, deptRes]) => {
      setData(balRes.data);
      setDepartments(deptRes.data || []);
    }).catch(console.error).finally(() => setLoading(false));
  };

  useEffect(() => { loadData(); }, []);

  const openEdit = (emp) => {
    setEditEmp(emp);
    setEffectiveMonth('');
    const cl = emp.balances?.casual_leave || {};
    const el = emp.balances?.earned_leave || {};
    const sl = emp.balances?.sick_leave || {};
    setEditForm({
      casual_leave: { entitled: cl.entitled ?? 0, used: cl.used ?? 0, balance: cl.balance ?? 0 },
      earned_leave: { entitled: el.entitled ?? 0, used: el.used ?? 0, balance: el.balance ?? 0 },
      sick_leave:   { entitled: sl.entitled ?? 0, used: sl.used ?? 0, balance: sl.balance ?? 0 },
    });
  };

  const updateField = (bucket, field, raw) => {
    const v = raw === '' ? 0 : parseFloat(raw);
    setEditForm(f => ({ ...f, [bucket]: { ...f[bucket], [field]: isNaN(v) ? 0 : v } }));
  };

  const handleSave = async () => {
    if (!editEmp || !editForm) return;
    setSaving(true);
    try {
      await api.put(`/api/leave/balances/${editEmp.id}`, { ...editForm, ...(effectiveMonth ? { effective_month: effectiveMonth } : {}) });
      toast.success(`Balances updated for ${editEmp.name}`);
      setEditEmp(null);
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update balances');
    } finally {
      setSaving(false);
    }
  };

  const openAudit = async (emp) => {
    setAuditEmp(emp);
    setAuditLoading(true);
    setAuditData(null);
    try {
      const r = await api.get(`/api/leave/admin/employee/${emp.id}/audit`);
      setAuditData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load audit');
      setAuditEmp(null);
    } finally {
      setAuditLoading(false);
    }
  };

  const filtered = deptFilter === 'All' ? data : data.filter(e => e.department === deptFilter);

  if (loading) return <Skeleton className="h-40" />;
  return (
    <div data-testid="all-balances-table">
      <div className="flex items-center gap-2 p-3 border-b border-slate-100">
        <Select value={deptFilter} onValueChange={setDeptFilter}>
          <SelectTrigger className="w-[200px] h-8 text-xs" data-testid="balance-dept-filter">
            <SelectValue placeholder="All Departments" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="All">All Departments</SelectItem>
            {departments.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="text-xs text-slate-400 ml-auto">{filtered.length} employees</span>
      </div>
      {filtered.length === 0 ? (
        <p className="text-sm text-slate-400 p-6 text-center">No data for selected department</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead>Code</TableHead>
              <TableHead>Employee</TableHead>
              <TableHead>Department</TableHead>
              <TableHead className="text-center">CL (Used/Balance/Entitled)</TableHead>
              <TableHead className="text-center">EL (Used/Balance/Entitled)</TableHead>
              <TableHead className="text-center">SL (Used/Balance/Entitled)</TableHead>
              {admin && <TableHead className="text-center w-[60px]">Edit</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map(e => (
              <TableRow key={e.id} data-testid={`balance-row-${e.id}`}>
                <TableCell className="text-xs text-slate-500 font-mono">{e.employee_code}</TableCell>
                <TableCell className="text-sm font-medium">{e.name}</TableCell>
                <TableCell className="text-sm text-slate-600">{e.department}</TableCell>
                <TableCell className="text-center text-sm font-tabular">{e.balances?.casual_leave?.used ?? 0}/{e.balances?.casual_leave?.balance ?? 0}/{e.balances?.casual_leave?.entitled ?? 0}</TableCell>
                <TableCell className="text-center text-sm font-tabular">{e.balances?.earned_leave?.used ?? 0}/{e.balances?.earned_leave?.balance ?? 0}/{e.balances?.earned_leave?.entitled ?? 0}</TableCell>
                <TableCell className="text-center text-sm font-tabular">{e.balances?.sick_leave?.used ?? 0}/{e.balances?.sick_leave?.balance ?? 0}/{e.balances?.sick_leave?.entitled ?? 0}</TableCell>
                {admin && (
                  <TableCell className="text-center">
                    <div className="flex gap-1 justify-center">
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openAudit(e)} data-testid={`audit-balance-${e.id}`} title="View leave history">
                        <Eye className="w-4 h-4 text-blue-700" />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEdit(e)} data-testid={`edit-balance-${e.id}`} title="Edit balances">
                        <Pencil className="w-4 h-4 text-[#0D2850]" />
                      </Button>
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={!!editEmp} onOpenChange={v => { if (!v) setEditEmp(null); }}>
        <DialogContent className="sm:max-w-lg" data-testid="edit-balance-dialog">
          <DialogHeader>
            <DialogTitle>Edit Leave Balances</DialogTitle>
            <DialogDescription>
              {editEmp?.name} ({editEmp?.employee_code}) — {editEmp?.department}
              {editEmp?.last_accrual_month ? <span className="block text-[11px] text-slate-400 mt-0.5">Last auto-accrual: {editEmp.last_accrual_month}</span> : null}
            </DialogDescription>
          </DialogHeader>
          {editForm && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-md p-2.5 text-[11px] text-slate-700">
                <strong>How this works:</strong> Only <strong>Entitled</strong> is editable. <em>Used</em> is auto-computed from approved leaves. <em>Balance</em> = Entitled − Used and updates automatically after you save.
              </div>
              <div>
                <Label className="text-[11px] text-slate-500">Effective from month <span className="text-slate-400">(optional)</span></Label>
                <Input type="month" value={effectiveMonth} onChange={ev => setEffectiveMonth(ev.target.value)} className="h-8 text-sm font-tabular" data-testid="edit-effective-month" />
                <p className="text-[10px] text-slate-400 mt-1">Default: current month. Pick an earlier month (e.g. 2026-09) to make this entitlement count for that month's payroll — clearing excess-leave LOP there.</p>
              </div>
              {[
                { key: 'casual_leave', label: 'Casual Leave (CL)', color: 'text-blue-700' },
                { key: 'earned_leave', label: 'Earned Leave (EL)', color: 'text-emerald-700' },
                { key: 'sick_leave',   label: 'Sick Leave (SL)',   color: 'text-amber-700' },
              ].map(b => {
                const entitledVal = editForm[b.key].entitled;
                const usedVal = editForm[b.key].used;
                const derivedBalance = Math.round(((parseFloat(entitledVal) || 0) - (parseFloat(usedVal) || 0)) * 100) / 100;
                return (
                  <div key={b.key} className="border border-slate-200 rounded-md p-3">
                    <p className={`text-xs font-semibold mb-2 ${b.color}`}>{b.label}</p>
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <Label className="text-[11px] text-slate-500">Entitled <span className="text-emerald-600">(editable)</span></Label>
                        <Input type="number" step="0.5" min="0" value={entitledVal} onChange={ev => updateField(b.key, 'entitled', ev.target.value)} className="h-8 text-sm font-tabular border-emerald-300 focus:border-emerald-500" data-testid={`edit-${b.key}-entitled`} />
                      </div>
                      <div>
                        <Label className="text-[11px] text-slate-500">Used (auto)</Label>
                        <Input type="number" value={usedVal} readOnly disabled className="h-8 text-sm font-tabular bg-slate-100 cursor-not-allowed" data-testid={`edit-${b.key}-used`} />
                      </div>
                      <div>
                        <Label className="text-[11px] text-slate-500">Balance (derived)</Label>
                        <Input type="number" value={derivedBalance} readOnly disabled className={`h-8 text-sm font-tabular bg-slate-100 cursor-not-allowed ${derivedBalance < 0 ? 'text-red-600 border-red-300' : ''}`} data-testid={`edit-${b.key}-balance`} />
                      </div>
                    </div>
                    {derivedBalance < 0 && (
                      <p className="text-[11px] text-red-600 mt-1.5">⚠️ Entitled ({entitledVal}) is less than Used ({usedVal}) — set Entitled to at least {usedVal} to avoid a negative balance.</p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditEmp(null)} data-testid="cancel-balance-edit">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 gap-1" data-testid="save-balance-edit">
              <Save className="w-3.5 h-3.5" />{saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Audit Modal — full leave history & reconciliation */}
      <Dialog open={!!auditEmp} onOpenChange={v => { if (!v) { setAuditEmp(null); setAuditData(null); } }}>
        <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="audit-balance-dialog">
          <DialogHeader>
            <DialogTitle>Leave History & Audit — {auditEmp?.name}</DialogTitle>
            <DialogDescription>
              Stored balance vs computed-from-leaves. Use this to spot any extra/duplicate leaves contributing to "used".
            </DialogDescription>
          </DialogHeader>
          {auditLoading && <Skeleton className="h-40" />}
          {auditData && (
            <div className="space-y-4">
              {/* Reconciliation summary */}
              <div className="border border-slate-200 rounded p-3 bg-slate-50">
                <p className="text-xs font-semibold text-slate-700 mb-2">Stored vs Computed</p>
                <Table className="text-xs">
                  <TableHeader>
                    <TableRow className="bg-white">
                      <TableHead className="text-xs">Type</TableHead>
                      <TableHead className="text-xs text-center">Entitled</TableHead>
                      <TableHead className="text-xs text-center">Stored Used</TableHead>
                      <TableHead className="text-xs text-center">Approved Days (sum)</TableHead>
                      <TableHead className="text-xs text-center">Excess (LOP)</TableHead>
                      <TableHead className="text-xs text-center">Computed Used</TableHead>
                      <TableHead className="text-xs text-center">Match?</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {Object.entries(auditData.reconciliation || {}).map(([lt, r]) => {
                      const ok = r.checks.used_matches_leaves;
                      return (
                        <TableRow key={lt}>
                          <TableCell className="text-xs font-medium">{lt}</TableCell>
                          <TableCell className="text-xs text-center font-tabular">{r.stored.entitled}</TableCell>
                          <TableCell className="text-xs text-center font-tabular">{r.stored.used}</TableCell>
                          <TableCell className="text-xs text-center font-tabular">{r.from_leaves.total_approved_days}</TableCell>
                          <TableCell className="text-xs text-center font-tabular text-red-500">{r.from_leaves.total_excess_days}</TableCell>
                          <TableCell className="text-xs text-center font-tabular text-emerald-700 font-medium">{r.from_leaves.expected_used}</TableCell>
                          <TableCell className="text-xs text-center">
                            {ok ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-[10px]">OK</Badge> : <Badge className="bg-red-100 text-red-700 hover:bg-red-100 text-[10px]">Δ {r.checks.discrepancy_used_vs_leaves}</Badge>}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              {/* Approved leaves listing */}
              <div className="border border-slate-200 rounded p-3">
                <p className="text-xs font-semibold text-slate-700 mb-2">All Approved Leaves ({(auditData.leaves || []).filter(l => l.status === 'approved').length})</p>
                {(auditData.leaves || []).filter(l => l.status === 'approved').length === 0 ? (
                  <p className="text-xs text-slate-400 italic py-3 text-center">No approved leaves found.</p>
                ) : (
                  <Table className="text-xs">
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="text-xs">Type</TableHead>
                        <TableHead className="text-xs">Date Range</TableHead>
                        <TableHead className="text-xs text-center">Days</TableHead>
                        <TableHead className="text-xs text-center">Excess (LOP)</TableHead>
                        <TableHead className="text-xs text-center">Counts as Used</TableHead>
                        <TableHead className="text-xs">Reason</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(auditData.leaves || [])
                        .filter(l => l.status === 'approved')
                        .map((lv, i) => {
                          const exc = lv.excess_days || 0;
                          const counts = (lv.days || 0) - exc;
                          return (
                            <TableRow key={i} data-testid={`audit-leave-${i}`}>
                              <TableCell className="text-xs"><Badge className="text-[10px]">{lv.leave_type}</Badge></TableCell>
                              <TableCell className="text-xs font-tabular">{lv.start_date} → {lv.end_date}{lv.half_day ? ' (½)' : ''}</TableCell>
                              <TableCell className="text-xs text-center font-tabular">{lv.days}</TableCell>
                              <TableCell className="text-xs text-center font-tabular text-red-500">{exc || '-'}</TableCell>
                              <TableCell className="text-xs text-center font-tabular text-emerald-700 font-medium">{counts}</TableCell>
                              <TableCell className="text-xs text-slate-500 truncate max-w-[200px]" title={lv.reason}>{lv.reason || '—'}</TableCell>
                            </TableRow>
                          );
                        })}
                    </TableBody>
                  </Table>
                )}
              </div>

              {/* MLP / Comp-Off mention */}
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div className="border border-slate-200 rounded p-2">
                  <p className="font-semibold text-pink-700 mb-1">MLP records: {(auditData.mlp_records || []).length}</p>
                  <p className="text-slate-400 text-[11px]">MLP doesn't deduct CL/EL/SL — stored separately.</p>
                </div>
                <div className="border border-slate-200 rounded p-2">
                  <p className="font-semibold text-emerald-700 mb-1">Comp-Offs: {(auditData.comp_offs || []).length}</p>
                  <p className="text-slate-400 text-[11px]">Earned by working ≥8h on Sun/holiday — separate pool.</p>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setAuditEmp(null); setAuditData(null); }} data-testid="close-audit">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
